import React, { useState, useRef } from 'react';
import {
  IonButton,
  IonFabButton,
  IonIcon,
  IonContent,
  IonPage,
  IonAlert,
  IonGrid,
  IonRow,
  IonCol,
  IonLoading,
  IonText,
  IonList,
  IonThumbnail,
  IonImg,
  IonItem
} from '@ionic/react';
import './UploadPrescription.scss';
import { addOutline, informationCircleOutline } from 'ionicons/icons';
import { AppointmentStepProps } from '../../navigators/AppointmentNavigator';
import Stepper from '../../components/Stepper';
import AppHeader from '../../components/AppHeader';
import { Appointment } from '../../models';
import { AppointmentsRootState } from '../../store/reducers/appointments';
import { setAppointment } from '../../store/actions/appointments';
import { connect } from 'react-redux';
import FileRepository from '../../repositories/FileRepository';
import EventBus from '../../services/EventBus';
import addFile from '../../assets/icons/addFile.svg';
import UploadPrescriptionChecker from './UploadPrescriptionChecker';
import { Prescription } from '../../repositories/FileRepository';

const mapStateToProps = (
  state: { appointments: AppointmentsRootState },
  ownProps: any
) => {
  return {
    laboratory: state.appointments.appointment.laboratory
  };
};

const mapDispatchToProps = (dispatch: Function, ownProps: any) => ({
  setAppointment: (appointment: Partial<Appointment>) =>
    dispatch(setAppointment(appointment))
});

type Props = AppointmentStepProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> & {
    isUploadMandatory: boolean;
    isCovid: boolean;
    titleLabel: string;
    subtitleLabel: string;
    explanationLabel: string;
    continueButtonLabel: string;
  };

const UploadAndCheckPrescription: React.FC<Props> = ({
  step,
  history,
  nextStepRouterLink = '/',
  setAppointment,
  isUploadMandatory = false,
  isCovid = false,
  laboratory,
  titleLabel = laboratory?.prescription_title
    ? laboratory?.prescription_title
    : 'Ajouter une ordonnance',
  subtitleLabel = laboratory?.prescription_description
    ? laboratory?.prescription_description
    : 'un fichier PDF ou une photo de votre ordonnance',
  explanationLabel = 'Pour un traitement optimal de votre ordonnance (1 photo par document joint), cadrez votre feuille aux quatres coins de celle-ci en la positionnant bien à plat.',
  continueButtonLabel = 'Continuer sans ordonnance',
  stepperComponent = <Stepper step={step} />
}) => {
  const [showReasonAlert, setShowReasonAlert] = useState(false);

  const [fileNames, setFileNames] = useState<string[]>([]);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [responseList, setResponseList] = useState<Prescription[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const allUploadsAreValid =
    responseList.filter(({ success }) => success === false).length === 0;

  function processFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) {
      if (
        e.target.files[0].type === 'image/jpg' ||
        e.target.files[0].type === 'image/jpeg' ||
        e.target.files[0].type === 'image/png' ||
        e.target.files[0].type === 'application/pdf'
      ) {
        const fileMatch = e.target.value.match(/[^\\/]*$/);

        if (fileMatch) {
          const fileName = fileMatch[0].replace(/\.pdf$/, '.jpg');
          setFileNames([...fileNames, fileName]);
        }
      } else {
        EventBus.error('Type de fichier non supporté');
        return;
      }
      setSubmitLoading(true);

      FileRepository.checkPrescription(e.target.files[0])
        .then((res) => {
          setResponseList([...responseList, res]);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setSubmitLoading(false);
        });
    }
    e.target.value = '';
  }

  function replaceFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const lastFileIndex = responseList.length - 1;

    if (e.target.files && e.target.files[0]) {
      if (
        e.target.files[0].type === 'image/jpg' ||
        e.target.files[0].type === 'image/jpeg' ||
        e.target.files[0].type === 'image/png' ||
        e.target.files[0].type === 'application/pdf'
      ) {
        const fileMatch = e.target.value.match(/[^\\/]*$/);

        if (fileMatch) {
          const fileName = fileMatch[0].replace(/\.pdf$/, '.jpg');
          setFileNames([...fileNames, fileName]);
        }
      } else {
        EventBus.error('Type de fichier non supporté');
        return;
      }

      setSubmitLoading(true);

      FileRepository.checkPrescription(e.target.files[0])
        .then((res) => {
          responseList[lastFileIndex] = res;
          setResponseList([...responseList]);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setSubmitLoading(false);
        });
    }
    e.target.value = '';
  }

  function removeFile(index: number) {
    const newResponseList = responseList.filter((_, i) => i !== index);
    setResponseList(newResponseList);
    setFileNames(fileNames.filter((_, i) => i !== index));
  }

  async function toFile() {
    const blobs = await Promise.all(
      responseList.map((prescription) => {
        return fetch(prescription.image).then((res) => res.blob());
      })
    );
    return blobs.map((blob, i) => {
      return new File([blob], fileNames[i]);
    });
  }

  async function onSubmitFiles() {
    setSubmitLoading(true);

    FileRepository.upload(await toFile())
      .then((files) => {
        setAppointment({ documents: files });
        history.push(nextStepRouterLink);
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  }

  function displayAddOrdonnance() {
    if (responseList.length === 0) {
      return (
        <label
          htmlFor="prescriptionFile"
          className="fab-add-prescription__label"
        >
          <IonFabButton
            class="fab-add-prescription"
            mode="md"
            is="div"
            id="open-modal"
          >
            <IonIcon icon={addOutline} />
          </IonFabButton>
        </label>
      );
    }
  }

  function displayMoreButtons() {
    return (
      <div className="ion-margin-start">
        <IonList lines="none" class="buttons" color="none" className="buttons">
          <IonItem color="none">
            <IonButton
              class="fab-add-prescription"
              onClick={(e) => {
                e.preventDefault();
                onSubmitFiles();
              }}
              mode="md"
              is="div"
              size="default"
              data-cy="validatePrescription"
            >
              Valider mon ordonnance
            </IonButton>
          </IonItem>
          <IonItem color="none">
            <IonButton
              size="default"
              mode="md"
              is="div"
              color="light"
              class="fab-add-prescription"
            >
              <label
                htmlFor="prescriptionReplaceFile"
                className="fab-add-prescription__label"
              >
                Changer d'ordonnance
              </label>
            </IonButton>
          </IonItem>
          <IonItem color="none">
            <IonButton
              size="default"
              mode="md"
              is="div"
              color="light"
              class="fab-add-prescription"
              id="open-modal"
              disabled={responseList.length === 3 ? true : false}
            >
              <label
                htmlFor="prescriptionFile"
                className="fab-add-prescription__label"
              >
                Ajouter une ordonnance
              </label>
            </IonButton>
          </IonItem>
        </IonList>
      </div>
    );
  }

  function displayErrorButton() {
    return (
      <>
        <IonButton
          class="fab-add-prescription"
          mode="md"
          is="div"
          size="default"
        >
          <label
            htmlFor="prescriptionReplaceFile"
            className="fab-add-prescription__label"
          >
            Renvoyer une nouvelle ordonnance
          </label>
        </IonButton>
      </>
    );
  }

  function displayPrescriptionButtons() {
    if (allUploadsAreValid) return displayMoreButtons();
    return displayErrorButton();
  }

  return (
    <IonPage>
      <AppHeader />
      {stepperComponent}
      <IonContent>
        <IonLoading isOpen={submitLoading} />
        <div className="wrapper upload-wrapper">
          <div className="ion-w ion-text-center">
            {laboratory?.prescription_message ? (
              <div className="booking-calendar__info booking-calendar__info--large ion-margin-bottom">
                <IonText>
                  <IonRow className="ion-align-items-center">
                    <IonCol size="auto">
                      <IonIcon size="large" icon={informationCircleOutline} />
                    </IonCol>
                    <IonCol className="prescription-body">
                      <IonRow>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: laboratory.prescription_message
                          }}
                        ></span>
                      </IonRow>
                      {laboratory?.prescription_popup && (
                        <>
                          <IonRow>
                            <IonButton
                              size="small"
                              onClick={() => setAlertOpen(true)}
                            >
                              En savoir plus
                            </IonButton>
                          </IonRow>
                        </>
                      )}
                    </IonCol>
                  </IonRow>
                </IonText>
                {laboratory?.prescription_popup && (
                  <IonAlert
                    cssClass={'prescription-popup'}
                    isOpen={isAlertOpen}
                    onDidDismiss={() => setAlertOpen(false)}
                    message={laboratory?.prescription_popup}
                    buttons={[
                      {
                        text: "D'accord",
                        handler: () => {}
                      }
                    ]}
                  />
                )}
              </div>
            ) : null}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf, image/png, image/jpeg"
              id="prescriptionFile"
              className="prescription-file"
              onChange={(e) => processFileUpload(e)}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf, image/png, image/jpeg"
              id="prescriptionReplaceFile"
              className="prescription-file"
              onChange={(e) => replaceFileUpload(e)}
            />
            <div
              style={{
                display: 'block',
                margin: '0 auto'
              }}
            >
              {!responseList.length ? (
                <>
                  <h2>{titleLabel}</h2>
                  <p>{subtitleLabel}</p>
                  <IonThumbnail class="centerTh">
                    <IonImg class="addFileImg" src={addFile} alt="fichier" />
                  </IonThumbnail>
                </>
              ) : submitLoading ? (
                <IonLoading isOpen={submitLoading} />
              ) : null}
            </div>
            <IonGrid className="ion-margin-bottom">
              <IonRow>
                {responseList.map((response, index) => (
                  <UploadPrescriptionChecker
                    prescription={response}
                    fileName={fileNames[index]}
                    removeFile={removeFile}
                    index={index}
                    key={index}
                  />
                ))}
              </IonRow>
            </IonGrid>
            {!isUploadMandatory ? (
              <IonButton
                onClick={() => {
                  if (isCovid) {
                    setAppointment({ documents: undefined });
                    history.push(nextStepRouterLink);
                  } else {
                    setShowReasonAlert(true);
                  }
                }}
                fill={responseList.length ? 'clear' : 'solid'}
              >
                {continueButtonLabel}
              </IonButton>
            ) : null}

            <div className="file-buttons">
              {responseList.length > 0
                ? displayPrescriptionButtons()
                : displayAddOrdonnance()}
            </div>
            <div>
              <p>{explanationLabel}</p>
            </div>

            <IonAlert
              isOpen={showReasonAlert}
              onDidDismiss={() => setShowReasonAlert(false)}
              inputs={[
                {
                  name: 'reason',
                  type: 'textarea',
                  placeholder: 'Raison de votre visite',
                  attributes: {
                    required: true,
                    autoFocus: true,
                    autofocus: true,
                    minLength: 1
                  }
                }
              ]}
              header={'Veuillez nous expliquer la raison de votre visite.'}
              buttons={[
                {
                  text: 'Annuler',
                  role: 'cancel'
                },
                {
                  text: 'Valider',
                  handler: (values) => {
                    if (values.reason && values.reason.length >= 1) {
                      setAppointment({ reason: values.reason });
                      history.push(nextStepRouterLink);
                    } else {
                      return false;
                    }
                  }
                }
              ]}
            />
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UploadAndCheckPrescription);
